import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, Button, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import "./App.css";
import tokenManager from "./services/tokenManager";
import { refreshAccessToken } from "./services/apiClient";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HotelProvider } from "./context/HotelContext";

import Account from "./pages/Account";
import Login from "./pages/Login";
import HotelOnboarding from "./pages/HotelOnboarding";
import HotelAdd_Information_1 from "./pages/HotelAdd_Information_1";
import HotelAdd_Information_2 from "./pages/HotelAdd_Information_2";
import HotelAdd_Information_3 from "./pages/HotelAdd_Information_3";
import HotelAdd_Information_4 from "./pages/HotelAdd_Information_4";
import HotelAdd_Information_5 from "./pages/HotelAdd_Information_5";
import HotelAdd_LicenseVerification from "./pages/HotelAdd_LicenseVerification";
import HotelAdd_LicVerSubmit from "./pages/HotelAdd_LicVerSubmit";
import HotelAdd_RoomInfo_1 from "./pages/HotelAdd_RoomInfo_1";
import HotelAdd_RoomInfo_2 from "./pages/HotelAdd_RoomInfo_2";
import HotelAdd_RoomInfo_3 from "./pages/HotelAdd_RoomInfo_3";
import HotelAdd_RoomInfoAdd_1 from "./pages/HotelAdd_RoomInfoAdd_1";
import HotelAdd_RoomInfoAdd_2 from "./pages/HotelAdd_RoomInfoAdd_2";
import HotelAdd_WPayment_1 from "./pages/HotelAdd_WPayment_1";
import HotelAdd_WPayment_2 from "./pages/HotelAdd_WPayment_2";
import HotelAdd_WPayment_Review from "./pages/HotelAdd_WPayment_Review";
import HotelAdd_YOverview from "./pages/HotelAdd_YOverview";
import HotelAdd_YSubmit from "./pages/HotelAdd_YSubmit";
import AdminReview from "./pages/AdminReview";
import AdminListHotels from "./pages/AdminListHotels";

const ACCESS_TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const LOGGED_OUT_FLAG = "ptag_logged_out";

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpiredDialog(true);
    };

    window.addEventListener("auth:session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("auth:session-expired", handleSessionExpired);
    };
  }, []);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      // Check if token exists in memory
      let token = tokenManager.getAuthToken();

      // If not in memory, try cookies (backward compatibility)
      if (!token) {
        token = Cookies.get('token');
        if (token) {
          const userId = Cookies.get('id');
          const userRole = Cookies.get('role');
          tokenManager.setAuthToken(token);
          tokenManager.setUserId(userId);
          tokenManager.setUserRole(userRole);
        }
      }

      // If still no token, try to refresh from refresh token cookie
      if (!token) {
        if (localStorage.getItem(LOGGED_OUT_FLAG) === "true") {
          setIsInitialized(true);
          return;
        }

        console.log('No token found, attempting refresh...');
        const newToken = await refreshAccessToken();
        if (newToken) {
          console.log('Session restored via refresh token');
        } else {
          console.log('No valid session found');
        }
      } else {
        console.log('Session restored from memory');
      }

      setIsInitialized(true);
    };

    restoreSession();
  }, []);

  useEffect(() => {
    if (!isInitialized) return undefined;

    const refreshActiveSession = async () => {
      const token = tokenManager.getAuthToken() || Cookies.get('token');
      const userId = tokenManager.getUserId() || Cookies.get('id');
      if (localStorage.getItem(LOGGED_OUT_FLAG) === "true" || !token || !userId) return;

      const newToken = await refreshAccessToken();
      if (newToken) {
        Cookies.set("token", newToken);
      } else {
        // Refresh failed (no valid refresh token) - session is gone
        setShowSessionExpiredDialog(true);
      }
    };

    const refreshInterval = setInterval(refreshActiveSession, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(refreshInterval);
  }, [isInitialized]);

  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="app">
      <Helmet>
        <title>Pet Hotel Admin</title>
        <meta property="og:title" content="PetHotelAdmin" />
        <meta property="og:site_name" content="PetHotelAdmin" />
        <meta property="og:url" content="https://www.pethoteladmin.com.hk/" />
        <meta
          property="og:image"
          content="https://www.ptag.com.hk/img/fb_display.jpg"
        />
        <meta
          property="og:description"
          content="關懷原於這裡，一個混合科技與藝術的寵物牌"
        />
        <meta property="fb:app_id" content="607616469433776" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <link
          rel="alternate"
          hreflang="vi"
          href="https://vi-vn.facebook.com/"
        />
        <meta
          name="keywords"
          content="Pet, PTag, Pet Tag, Lost, Protect, Protection, 寵物, 寵物牌, 保護, 保障, 智能寵物牌, 健康, 失寵, 失狗, 失貓, 尋狗, 尋貓, 尋寵, 寵物展, 推介產品, 寵物名牌, 寵物走失, 自訂寵物牌"
        />
        <meta
          name="description"
          content="PTag - 關懷，原於這裡。一個混合科技與藝術的寵物名牌。體積很細，功能很大。PTag，一個寵物資料庫，給你的愛寵多一個保障。"
        />
        <meta name="robots" content="noodp,noydir" />
        <meta name="author" content="Pet Boys Club, 寵物仔仔" />
      </Helmet>

      <BrowserRouter>
        <HotelProvider>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/account/:id" element={<Account />} />
            <Route path="/hotel/onboarding" element={<HotelOnboarding />} />
            <Route path="/hotel/add/info1/:id" element={<HotelAdd_Information_1 />} />
            <Route path="/hotel/add/info2/:id" element={<HotelAdd_Information_2 />} />
            <Route path="/hotel/add/info3/:id" element={<HotelAdd_Information_3 />} />
            <Route path="/hotel/add/info4/:id" element={<HotelAdd_Information_4 />} />
            <Route path="/hotel/add/info5/:id" element={<HotelAdd_Information_5 />} />
            <Route path="/hotel/add/license-verification/:id" element={<HotelAdd_LicenseVerification />} />
            <Route path="/hotel/add/license-verification-submit/:id" element={<HotelAdd_LicVerSubmit />} />
            <Route path="/hotel/add/room-info-1/:id" element={<HotelAdd_RoomInfo_1 />} />
            <Route path="/hotel/add/room-info-2/:id" element={<HotelAdd_RoomInfo_2 />} />
            <Route path="/hotel/add/room-info-3/:id" element={<HotelAdd_RoomInfo_3 />} />
            <Route path="/hotel/add/room-info-add-1/:id" element={<HotelAdd_RoomInfoAdd_1 />} />
            <Route path="/hotel/add/room-info-add-2/:id" element={<HotelAdd_RoomInfoAdd_2 />} />
            <Route path="/hotel/add/payment-1/:id" element={<HotelAdd_WPayment_1 />} />
            <Route path="/hotel/add/payment-2/:id" element={<HotelAdd_WPayment_2 />} />
            <Route path="/hotel/add/payment-review/:id" element={<HotelAdd_WPayment_Review />} />
            <Route path="/hotel/add/overview/:id" element={<HotelAdd_YOverview />} />
            <Route path="/hotel/add/submit/:id" element={<HotelAdd_YSubmit />} />
            <Route path="/admin/list-hotels" element={<AdminListHotels />} />
            <Route path="/admin-review/:hotelId" element={<AdminReview />} />
          </Routes>
        </HotelProvider>
      </BrowserRouter>

      <Dialog
        open={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 400,
            width: "100%",
            p: 3,
            m: 2,
            boxSizing: "border-box",
            textAlign: "center",
          },
        }}
      >
        <DialogContent sx={{ pt: 0, pb: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: 20, mb: 1 }}
          >
            {t("SessionExpiredTitle")}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 16, color: "#666" }}>
            {t("SessionExpiredMessage")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: "column",
            "& > :not(style) ~ :not(style)": {
              marginLeft: 0,
            },
          }}
        >
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#31BACD",
              color: "#050505",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              mb: 1,
              textAlign: "center",
              textTransform: "none",
              "&:hover": { backgroundColor: "#85bec5" },
            }}
            onClick={() => {
              // Clear tokens from memory (primary storage)
              tokenManager.clear();
              localStorage.setItem(LOGGED_OUT_FLAG, "true");

              // Clear cookies (backward compatibility)
              Cookies.remove("token");
              Cookies.remove("id");
              Cookies.remove("userRole");
              Cookies.remove("refreshToken");
              Cookies.remove("refreshToken", { path: "/" });
              Cookies.remove("refreshToken", { path: "/auth/tokens/refresh" });
              Cookies.remove("refreshToken", { path: "/development/auth/tokens/refresh" });
              Cookies.remove("notifications");
              Cookies.remove("role");

              // Update UI state
              setShowSessionExpiredDialog(false);
              window.location.href = "/";
            }}
          >
            {t("loginFooter")}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{
              color: "#B0B0B0",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 2,
              py: 1,
              textAlign: "center",
              textTransform: "none",
            }}
            onClick={() => setShowSessionExpiredDialog(false)}
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default App;
